import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Stack, Button, Chip, CircularProgress, Snackbar, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, TableSortLabel,
} from '@mui/material';
import {
  Add, Refresh, AccountTree, AccountBalanceWallet, Insights, TrendingUp, TrendingDown,
  Savings, Speed, Business, Edit, ArrowForward, ChevronRight, Layers, Groups,
} from '@mui/icons-material';
import {
  costProfitService, CostProfitSummary, CostCenterRow, ProfitCenterRow,
  CostCentersAnalytics, ProfitCentersAnalytics, CostCenterInput, ProfitCenterInput,
} from '../../services/costProfitService';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import {
  StatCard, TableSurface, headerCellSx, bodyRowSx, StatusDot, StatusTone, TypePill,
  TableEmptyState, ErrorState,
} from '../../components/common/pageKit';

const CC_TYPE_TINT: Record<string, string> = {
  PRODUCTION: '#6366f1', SALES: '#8b5cf6', CORPORATE: '#0891b2', 'R&D': '#0284c7',
  GENERAL: '#64748b', UNASSIGNED: '#94a3b8',
};
const PC_TYPE_TINT: Record<string, string> = { BUSINESS_UNIT: '#6366f1', SEGMENT: '#0891b2', REGION: '#059669' };

const utilTone = (u: number | null): StatusTone => (u == null ? 'neutral' : u > 100 ? 'error' : u >= 90 ? 'warning' : 'success');

const UtilBar: React.FC<{ value: number | null; color: string }> = ({ value, color }) => (
  <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.75}>
    <Box sx={{ width: 64, height: 6, borderRadius: 3, bgcolor: `${color}22`, overflow: 'hidden' }}>
      <Box sx={{ width: `${Math.min(Math.max(value || 0, 0), 100)}%`, height: '100%', bgcolor: color, borderRadius: 3 }} />
    </Box>
    <Typography variant="caption" sx={{ fontWeight: 700, color, minWidth: 40, textAlign: 'right' }}>{value != null ? `${value}%` : '—'}</Typography>
  </Stack>
);

type PcSortKey = 'rank' | 'revenue' | 'cogs' | 'grossProfit' | 'opex' | 'operatingProfit' | 'margin';

const CostProfitControlCenter: React.FC = () => {
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const line = `1px solid ${c.line}`;
  const panelSx = { borderRadius: 3, border: line, bgcolor: c.panel } as const;

  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState<CostProfitSummary | null>(null);
  const [ccAnalytics, setCcAnalytics] = useState<CostCentersAnalytics | null>(null);
  const [pcAnalytics, setPcAnalytics] = useState<ProfitCentersAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [pcSort, setPcSort] = useState<{ key: PcSortKey; dir: 'asc' | 'desc' }>({ key: 'rank', dir: 'asc' });

  // Create / edit dialog
  const [dlg, setDlg] = useState<{ kind: 'cost' | 'profit'; editId: number | null } | null>(null);
  const [ccForm, setCcForm] = useState<CostCenterInput>({});
  const [pcForm, setPcForm] = useState<ProfitCenterInput>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      costProfitService.summary(),
      costProfitService.costCentersAnalytics(),
      costProfitService.profitCentersAnalytics(),
    ])
      .then(([s, cca, pca]) => { setSummary(s); setCcAnalytics(cca); setPcAnalytics(pca); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load cost & profitability data'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const kpis = summary ? [
    { title: 'Cost Centers', color: '#6366f1', icon: <Business />, value: summary.totalCostCenters, footer: <Typography variant="caption" color="text.secondary">{summary.activeCostCenters} active</Typography> },
    { title: 'Profit Centers', color: '#8b5cf6', icon: <Groups />, value: summary.profitCenters, footer: <Typography variant="caption" color="text.secondary">business units</Typography> },
    { title: 'Total Budget', color: '#0891b2', icon: <AccountBalanceWallet />, value: formatIndianNumber(summary.totalBudget), footer: <Typography variant="caption" color="text.secondary">cost-centre plan</Typography> },
    { title: 'Actual Cost', color: '#ea580c', icon: <Insights />, value: formatIndianNumber(summary.actualCost), footer: <Typography variant="caption" color="text.secondary">posted, {summary.financialYear}</Typography> },
    { title: 'Revenue (PC)', color: '#059669', icon: <TrendingUp />, value: formatIndianNumber(summary.revenue), footer: <Typography variant="caption" color="text.secondary">attributed to profit centres</Typography> },
    { title: 'Operating Profit', color: summary.operatingProfit >= 0 ? '#0284c7' : '#dc2626', icon: summary.operatingProfit >= 0 ? <Savings /> : <TrendingDown />, value: formatIndianNumber(summary.operatingProfit), footer: <StatusDot label={summary.operatingMargin != null ? `${summary.operatingMargin}% margin` : '—'} tone={summary.operatingProfit >= 0 ? 'success' : 'error'} /> },
  ] : [];

  const ccRows = ccAnalytics?.rows || [];
  const pcRows = pcAnalytics?.rows || [];

  const sortedPc = useMemo(() => {
    const rows = [...pcRows];
    const { key, dir } = pcSort;
    rows.sort((a, b) => {
      const av = (a[key] ?? -Infinity) as number; const bv = (b[key] ?? -Infinity) as number;
      return dir === 'asc' ? av - bv : bv - av;
    });
    return rows;
  }, [pcRows, pcSort]);
  const toggleSort = (key: PcSortKey) => setPcSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  // ---- Hierarchy roll-up (parent_id) ----
  const hierarchy = useMemo(() => {
    const real = ccRows.filter((r) => r.id != null);
    const byId = new Map(real.map((r) => [r.id as number, r]));
    const children = new Map<number | null, CostCenterRow[]>();
    real.forEach((r) => {
      const p = r.parentId && byId.has(r.parentId) ? r.parentId : null;
      if (!children.has(p)) children.set(p, []);
      children.get(p)!.push(r);
    });
    // Recursive roll-up of budget + actual (self + descendants).
    const roll = (r: CostCenterRow): { budget: number; actual: number } => {
      const kids = children.get(r.id as number) || [];
      let budget = r.budget; let actual = r.actual;
      kids.forEach((k) => { const c2 = roll(k); budget += c2.budget; actual += c2.actual; });
      return { budget: Math.round(budget * 100) / 100, actual: Math.round(actual * 100) / 100 };
    };
    return { roots: children.get(null) || [], children, roll };
  }, [ccRows]);

  const openCreate = (kind: 'cost' | 'profit') => {
    setDlg({ kind, editId: null });
    if (kind === 'cost') setCcForm({ costCenterType: 'PRODUCTION', status: 'ACTIVE', isActive: true });
    else setPcForm({ pcType: 'BUSINESS_UNIT', status: 'ACTIVE', isActive: true });
  };
  const openEditCc = (r: CostCenterRow) => {
    setDlg({ kind: 'cost', editId: r.id as number });
    setCcForm({ code: r.code, name: r.name, costCenterType: r.type, parentId: r.parentId, managerName: r.managerName || '', plant: r.plant || '', status: r.status });
  };
  const openEditPc = (r: ProfitCenterRow) => {
    setDlg({ kind: 'profit', editId: r.id });
    setPcForm({ code: r.code, name: r.name, pcType: r.type, segment: r.segment || '', region: r.region || '', managerName: r.managerName || '', plant: r.plant || '', targetMargin: r.targetMargin ?? '', status: r.status });
  };

  const save = async () => {
    if (!dlg) return;
    setSaving(true);
    try {
      if (dlg.kind === 'cost') {
        if (!dlg.editId && (!ccForm.code || !ccForm.name)) throw new Error('Code and name are required');
        if (dlg.editId) await costProfitService.updateCostCenter(dlg.editId, ccForm);
        else await costProfitService.createCostCenter(ccForm);
      } else {
        if (!dlg.editId && (!pcForm.code || !pcForm.name)) throw new Error('Code and name are required');
        if (dlg.editId) await costProfitService.updateProfitCenter(dlg.editId, pcForm);
        else await costProfitService.createProfitCenter(pcForm);
      }
      setToast('Saved'); setDlg(null); load();
    } catch (e: any) { setToast(e.message || 'Save failed'); } finally { setSaving(false); }
  };

  const ccParents = ccRows.filter((r) => r.id != null);

  return (
    <Box sx={{ p: 3, bgcolor: c.bg, minHeight: '100%' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Cost &amp; Profitability Control Center</Typography>
          <Typography variant="body2" color="text.secondary">Cost-centre budget vs real posted actuals, and profit-centre P&amp;L margins — every figure traced to POSTED journals.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          <Button variant="outlined" startIcon={<Add />} onClick={() => openCreate('profit')}>New Profit Centre</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => openCreate('cost')}>New Cost Centre</Button>
        </Stack>
      </Stack>

      {/* KPI strip */}
      <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
        {(summary ? kpis : Array.from({ length: 6 })).map((k: any, i) => (
          <Grid size={{ xs: 6, sm: 4, md: 2 }} key={i}>
            {summary ? <StatCard title={k.title} value={k.value} icon={k.icon} color={k.color} footer={k.footer} />
              : <Paper elevation={0} sx={{ ...panelSx, height: 118, display: 'grid', placeItems: 'center' }}><CircularProgress size={20} /></Paper>}
          </Grid>
        ))}
      </Grid>

      {error && <Box sx={{ mb: 2 }}><ErrorState message={error} onRetry={load} /></Box>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2.5, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700 } }}>
        {['Overview', `Cost Centres (${ccRows.filter((r) => r.id).length})`, `Profit Centres (${pcRows.length})`, 'Hierarchy'].map((t) => <Tab key={t} label={t} />)}
      </Tabs>

      {/* ---------------- Overview ---------------- */}
      {tab === 0 && (
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
              <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Cost Centre Utilisation</Typography>
              <Stack spacing={1.25}>
                {[...ccRows].filter((r) => r.id).sort((a, b) => (b.utilisation || 0) - (a.utilisation || 0)).slice(0, 7).map((r) => {
                  const uc = c[utilTone(r.utilisation) === 'error' ? 'bad' : utilTone(r.utilisation) === 'warning' ? 'warn' : 'good'];
                  return (
                    <Box key={r.id}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{r.code} <Typography component="span" variant="caption" color="text.secondary">· {r.name}</Typography></Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: uc }}>{formatIndianNumber(r.actual)} / {formatIndianNumber(r.budget)}</Typography>
                      </Stack>
                      <Box sx={{ height: 7, borderRadius: 3, bgcolor: `${uc}22`, overflow: 'hidden' }}>
                        <Box sx={{ width: `${Math.min(r.utilisation || 0, 100)}%`, height: '100%', bgcolor: uc }} />
                      </Box>
                    </Box>
                  );
                })}
                {!ccRows.filter((r) => r.id).length && <Typography variant="body2" color="text.secondary">No cost centres yet.</Typography>}
              </Stack>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
              <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Profit Centre Margins vs Target</Typography>
              <Stack spacing={1.75}>
                {pcRows.map((r) => {
                  const m = r.margin || 0;
                  const good = r.marginVsTarget == null ? true : r.marginVsTarget >= 0;
                  const tint = good ? c.good : c.warn;
                  return (
                    <Box key={r.id} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/finance/profit-centers/${r.id}`)}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{r.code} <Typography component="span" variant="caption" color="text.secondary">· {r.name}</Typography></Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: tint }}>
                          {r.margin != null ? `${r.margin}%` : '—'}{r.targetMargin != null ? ` / ${r.targetMargin}% target` : ''}
                        </Typography>
                      </Stack>
                      <Box sx={{ position: 'relative', height: 8, borderRadius: 3, bgcolor: `${tint}22`, overflow: 'hidden' }}>
                        <Box sx={{ width: `${Math.min(Math.max(m, 0), 100)}%`, height: '100%', bgcolor: tint }} />
                        {r.targetMargin != null && (
                          <Box sx={{ position: 'absolute', top: -2, bottom: -2, left: `${Math.min(Math.max(r.targetMargin, 0), 100)}%`, width: 2, bgcolor: c.text }} />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Rev {formatIndianNumber(r.revenue)} · GP {formatIndianNumber(r.grossProfit)} · OP {formatIndianNumber(r.operatingProfit)}
                      </Typography>
                    </Box>
                  );
                })}
                {!pcRows.length && <Typography variant="body2" color="text.secondary">No profit centres yet.</Typography>}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ---------------- Cost Centres ---------------- */}
      {tab === 1 && (
        <TableSurface>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Code', 'Name', 'Type', 'Manager', 'Budget', 'Actual', 'Variance', 'Utilisation', 'Status', 'Actions'].map((h, i) => (
                    <TableCell key={h} sx={headerCellSx} align={i >= 4 && i <= 7 ? 'right' : i === 9 ? 'right' : 'left'}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && !ccRows.length ? (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ border: 0, py: 6 }}><CircularProgress /></TableCell></TableRow>
                ) : !ccRows.length ? (
                  <TableRow><TableCell colSpan={10} sx={{ border: 0 }}><TableEmptyState title="No cost centres yet" hint="Create your first cost centre to start tracking budget vs actual." /></TableCell></TableRow>
                ) : ccRows.map((r) => {
                  const tint = CC_TYPE_TINT[r.type] || c.accent;
                  const uc = c[utilTone(r.utilisation) === 'error' ? 'bad' : utilTone(r.utilisation) === 'warning' ? 'warn' : 'good'];
                  const clickable = r.id != null;
                  return (
                    <TableRow key={r.id ?? r.code} hover sx={{ ...(bodyRowSx as any), cursor: clickable ? 'pointer' : 'default' }}
                      onClick={() => clickable && navigate(`/finance/cost-centers/${r.id}`)}>
                      <TableCell><Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: tint }}>{r.code}</Typography></TableCell>
                      <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{r.name}</Typography>{r.departmentName && <Typography variant="caption" color="text.secondary">{r.departmentName}</Typography>}</TableCell>
                      <TableCell><TypePill label={r.type} tint={tint} /></TableCell>
                      <TableCell><Typography variant="body2" color="text.secondary">{r.managerName || '—'}</Typography></TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(r.budget)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCurrency(r.actual)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: r.variance < 0 ? c.bad : 'inherit' }}>{formatCurrency(r.variance)}</TableCell>
                      <TableCell align="right"><UtilBar value={r.utilisation} color={uc} /></TableCell>
                      <TableCell><StatusDot label={r.type === 'UNASSIGNED' ? 'Unassigned' : (r.isActive ? 'Active' : 'Inactive')} tone={r.type === 'UNASSIGNED' ? 'neutral' : (r.isActive ? 'success' : 'neutral')} /></TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {clickable && (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditCc(r)}><Edit sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                            <Tooltip title="Open 360"><IconButton size="small" onClick={() => navigate(`/finance/cost-centers/${r.id}`)}><ArrowForward sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {ccAnalytics && (
                  <TableRow sx={{ '& td': { fontWeight: 800, borderTop: `2px solid ${c.line}` } }}>
                    <TableCell colSpan={4}>Total</TableCell>
                    <TableCell align="right">{formatCurrency(ccAnalytics.totals.budget)}</TableCell>
                    <TableCell align="right">{formatCurrency(ccAnalytics.totals.actual)}</TableCell>
                    <TableCell align="right" sx={{ color: ccAnalytics.totals.variance < 0 ? c.bad : 'inherit' }}>{formatCurrency(ccAnalytics.totals.variance)}</TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* ---------------- Profit Centres ---------------- */}
      {tab === 2 && (
        <TableSurface>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Code</TableCell>
                  <TableCell sx={headerCellSx}>Name</TableCell>
                  {([['Revenue', 'revenue'], ['COGS', 'cogs'], ['Gross Profit', 'grossProfit'], ['Opex', 'opex'], ['Operating Profit', 'operatingProfit'], ['Margin', 'margin']] as [string, PcSortKey][]).map(([label, key]) => (
                    <TableCell key={key} sx={headerCellSx} align="right" sortDirection={pcSort.key === key ? pcSort.dir : false}>
                      <TableSortLabel active={pcSort.key === key} direction={pcSort.key === key ? pcSort.dir : 'desc'} onClick={() => toggleSort(key)}>{label}</TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell sx={headerCellSx} align="right">Target</TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    <TableSortLabel active={pcSort.key === 'rank'} direction={pcSort.key === 'rank' ? pcSort.dir : 'asc'} onClick={() => toggleSort('rank')}>Rank</TableSortLabel>
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && !pcRows.length ? (
                  <TableRow><TableCell colSpan={11} align="center" sx={{ border: 0, py: 6 }}><CircularProgress /></TableCell></TableRow>
                ) : !pcRows.length ? (
                  <TableRow><TableCell colSpan={11} sx={{ border: 0 }}><TableEmptyState title="No profit centres yet" hint="Create a profit centre and attribute revenue to see margins." /></TableCell></TableRow>
                ) : sortedPc.map((r) => {
                  const tint = PC_TYPE_TINT[r.type] || c.accent;
                  const marginGood = r.marginVsTarget == null ? (r.margin || 0) >= 0 : r.marginVsTarget >= 0;
                  return (
                    <TableRow key={r.id} hover sx={{ ...(bodyRowSx as any), cursor: 'pointer' }} onClick={() => navigate(`/finance/profit-centers/${r.id}`)}>
                      <TableCell><Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: tint }}>{r.code}</Typography></TableCell>
                      <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{r.name}</Typography>{(r.segment || r.region) && <Typography variant="caption" color="text.secondary">{[r.segment, r.region].filter(Boolean).join(' · ')}</Typography>}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(r.revenue)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(r.cogs)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCurrency(r.grossProfit)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(r.opex)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: r.operatingProfit >= 0 ? c.good : c.bad }}>{formatCurrency(r.operatingProfit)}</TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={r.margin != null ? `${r.margin}%` : '—'} sx={{ height: 22, fontWeight: 700, bgcolor: marginGood ? `${c.good}1f` : `${c.warn}1f`, color: marginGood ? c.good : c.warn }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: c.sub }}>{r.targetMargin != null ? `${r.targetMargin}%` : '—'}</TableCell>
                      <TableCell align="right"><Chip size="small" variant="outlined" label={`#${r.rank}`} sx={{ height: 22 }} /></TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditPc(r)}><Edit sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                          <Tooltip title="Open 360"><IconButton size="small" onClick={() => navigate(`/finance/profit-centers/${r.id}`)}><ArrowForward sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {pcAnalytics && pcRows.length > 0 && (
                  <TableRow sx={{ '& td': { fontWeight: 800, borderTop: `2px solid ${c.line}` } }}>
                    <TableCell colSpan={2}>Total</TableCell>
                    <TableCell align="right">{formatCurrency(pcAnalytics.totals.revenue)}</TableCell>
                    <TableCell align="right">{formatCurrency(pcAnalytics.totals.cogs)}</TableCell>
                    <TableCell align="right">{formatCurrency(pcAnalytics.totals.grossProfit)}</TableCell>
                    <TableCell align="right">{formatCurrency(pcAnalytics.totals.opex)}</TableCell>
                    <TableCell align="right">{formatCurrency(pcAnalytics.totals.operatingProfit)}</TableCell>
                    <TableCell align="right">{pcAnalytics.totals.margin != null ? `${pcAnalytics.totals.margin}%` : '—'}</TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* ---------------- Hierarchy ---------------- */}
      {tab === 3 && (
        <Paper elevation={0} sx={{ ...panelSx, p: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <AccountTree sx={{ color: c.accent }} />
            <Typography sx={{ fontWeight: 700 }}>Cost Centre Hierarchy — budget &amp; actual rolled up by parent</Typography>
          </Stack>
          {!hierarchy.roots.length ? <Typography variant="body2" color="text.secondary">No hierarchy defined.</Typography> : (
            <Stack spacing={0.5}>
              {hierarchy.roots.map((r) => <HierarchyNode key={r.id} node={r} depth={0} hierarchy={hierarchy} c={c} navigate={navigate} />)}
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Rolled-up figures sum a centre and all its descendants. Actuals come from POSTED journal lines stamped with each cost centre.
          </Typography>
        </Paper>
      )}

      {/* ---------------- Create / Edit dialog ---------------- */}
      <Dialog open={!!dlg} onClose={() => setDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {dlg?.editId ? 'Edit' : 'New'} {dlg?.kind === 'cost' ? 'Cost Centre' : 'Profit Centre'}
        </DialogTitle>
        <DialogContent>
          {dlg?.kind === 'cost' ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Code" fullWidth value={ccForm.code || ''} disabled={!!dlg?.editId} onChange={(e) => setCcForm({ ...ccForm, code: e.target.value })} />
                <TextField select size="small" label="Type" fullWidth value={ccForm.costCenterType || 'PRODUCTION'} onChange={(e) => setCcForm({ ...ccForm, costCenterType: e.target.value })}>
                  {['PRODUCTION', 'SALES', 'CORPORATE', 'R&D', 'GENERAL'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Stack>
              <TextField size="small" label="Name" fullWidth value={ccForm.name || ''} onChange={(e) => setCcForm({ ...ccForm, name: e.target.value })} />
              <TextField select size="small" label="Parent Cost Centre" fullWidth value={ccForm.parentId ?? ''} onChange={(e) => setCcForm({ ...ccForm, parentId: e.target.value === '' ? null : Number(e.target.value) })}>
                <MenuItem value="">— None (top level) —</MenuItem>
                {ccParents.filter((p) => p.id !== dlg?.editId).map((p) => <MenuItem key={p.id} value={p.id as number}>{p.code} · {p.name}</MenuItem>)}
              </TextField>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Manager" fullWidth value={ccForm.managerName || ''} onChange={(e) => setCcForm({ ...ccForm, managerName: e.target.value })} />
                <TextField size="small" label="Plant" fullWidth value={ccForm.plant || ''} onChange={(e) => setCcForm({ ...ccForm, plant: e.target.value })} />
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Code" fullWidth value={pcForm.code || ''} disabled={!!dlg?.editId} onChange={(e) => setPcForm({ ...pcForm, code: e.target.value })} />
                <TextField select size="small" label="Type" fullWidth value={pcForm.pcType || 'BUSINESS_UNIT'} onChange={(e) => setPcForm({ ...pcForm, pcType: e.target.value })}>
                  {['BUSINESS_UNIT', 'SEGMENT', 'REGION'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Stack>
              <TextField size="small" label="Name" fullWidth value={pcForm.name || ''} onChange={(e) => setPcForm({ ...pcForm, name: e.target.value })} />
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Segment" fullWidth value={pcForm.segment || ''} onChange={(e) => setPcForm({ ...pcForm, segment: e.target.value })} />
                <TextField size="small" label="Region" fullWidth value={pcForm.region || ''} onChange={(e) => setPcForm({ ...pcForm, region: e.target.value })} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Manager" fullWidth value={pcForm.managerName || ''} onChange={(e) => setPcForm({ ...pcForm, managerName: e.target.value })} />
                <TextField size="small" type="number" label="Target Margin %" fullWidth value={pcForm.targetMargin ?? ''} onChange={(e) => setPcForm({ ...pcForm, targetMargin: e.target.value })} />
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2800} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

// Recursive hierarchy row with roll-up.
const HierarchyNode: React.FC<{ node: CostCenterRow; depth: number; hierarchy: any; c: any; navigate: (p: string) => void }> = ({ node, depth, hierarchy, c, navigate }) => {
  const kids: CostCenterRow[] = hierarchy.children.get(node.id) || [];
  const rolled = hierarchy.roll(node);
  const util = rolled.budget ? Math.round((rolled.actual / rolled.budget) * 1000) / 10 : null;
  const uc = util == null ? c.sub : util > 100 ? c.bad : util >= 90 ? c.warn : c.good;
  const tint = CC_TYPE_TINT[node.type] || c.accent;
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1}
        sx={{ pl: depth * 3, py: 1, borderRadius: 2, cursor: 'pointer', '&:hover': { bgcolor: c.panel2 } }}
        onClick={() => navigate(`/finance/cost-centers/${node.id}`)}>
        {depth > 0 ? <ChevronRight sx={{ fontSize: 16, color: c.sub }} /> : <Layers sx={{ fontSize: 16, color: tint }} />}
        <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: tint, minWidth: 96 }}>{node.code}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.name}{kids.length ? <Typography component="span" variant="caption" color="text.secondary"> · {kids.length} sub</Typography> : null}</Typography>
        <Typography variant="caption" sx={{ color: c.sub, fontVariantNumeric: 'tabular-nums' }}>Bgt {formatIndianNumber(rolled.budget)}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: uc, minWidth: 130, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>Act {formatIndianNumber(rolled.actual)} ({util != null ? `${util}%` : '—'})</Typography>
      </Stack>
      {kids.map((k) => <HierarchyNode key={k.id} node={k} depth={depth + 1} hierarchy={hierarchy} c={c} navigate={navigate} />)}
    </Box>
  );
};

export default CostProfitControlCenter;
